import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table, Tabs, Tag, Slider } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import {
  getInventoryReportApi,
  getLifecycleReportApi, getVarianceReportApi,
  getLowStockApi, getDueReplacementsApi,
  getUniformItemsApi, uniformErrorMessage,
  exportInventoryReportApi, exportVarianceReportApi, exportDueReplacementsApi,
  exportLifecycleReportApi,
} from 'api/services/uniformManagement';
import { useUniformMgmt } from '../context/uniformMgmtContext';
import { formatDateHandler } from 'utils/common';
import PageHeader from 'components/PageHeader';
import ExcelExportButton from 'components/ExcelExportButton';
import ReportFilterBar, { cleanParams } from './ReportFilterBar';
import IssuanceReport from './tabs/IssuanceReport';
import EmployeeHistoryReport from './tabs/EmployeeHistoryReport';
import TransferReport from './tabs/TransferReport';
import PurchaseOrderReport from './tabs/PurchaseOrderReport';
import IncompleteSizeProfilesReport from './tabs/IncompleteSizeProfilesReport';
import useStickyIdentity from '../components/useStickyIdentity';
import './lifecycleMatrix.css';
import { UNIFORM_LANE, truncatedText } from '../components/columns';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

// Tab order per spec: Issuance (CASA-wise) is the default landing tab.
const ALL_TAB_KEYS = ['issuance', 'employee', 'size-profiles', 'transfers', 'purchase-orders', 'stock', 'lifecycle', 'variance', 'lowstock', 'due'];

/**
 * Tabs built but not shown to users.
 *
 * Low Stock reads reorder levels that are not yet set up as a working
 * process, so the report is honest about its data and still misleading to
 * read. Hidden rather than deleted: the widget, its endpoint and its filters
 * all stay, so restoring it is removing one string from this set.
 *
 * A hidden key is also dropped from the routable keys below, so an old
 * `?tab=lowstock` bookmark opens the default tab instead of a blank pane.
 */
const HIDDEN_TABS = new Set(['lowstock']);

const TAB_KEYS = ALL_TAB_KEYS.filter((k) => !HIDDEN_TABS.has(k));
const DEFAULT_TAB = 'issuance';

const ReportsDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(TAB_KEYS.includes(requestedTab) ? requestedTab : DEFAULT_TAB);

  // Uniform items are loaded once here and passed down to every tab that
  // needs an item filter, rather than each tab re-fetching the same ~1000 rows.
  const [itemOptions, setItemOptions] = useState([]);
  const [itemOptionsLoading, setItemOptionsLoading] = useState(false);

  useEffect(() => {
    let ignore = false;
    setItemOptionsLoading(true);
    getUniformItemsApi({ page_size: 1000 }).then((response) => {
      if (ignore) return;
      setItemOptions(
        (response.data?.results || [])
          .map((i) => ({ label: i.name, value: i.id }))
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      );
    }).catch((e) => {
      if (ignore) return;
      toast.error(uniformErrorMessage(e, 'Failed to load uniform items'), toastOpts);
    }).finally(() => { if (!ignore) setItemOptionsLoading(false); });
    return () => { ignore = true; };
  }, []);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', key);
      return next;
    }, { replace: true });
  };

  const tabItems = [
    { key: 'issuance', label: 'Issuance (Cost Center-wise)', children: <IssuanceReport itemOptions={itemOptions} itemOptionsLoading={itemOptionsLoading} /> },
    { key: 'employee', label: 'Employee History', children: <EmployeeHistoryReport itemOptions={itemOptions} itemOptionsLoading={itemOptionsLoading} /> },
    { key: 'size-profiles', label: 'Incomplete Size Profiles', children: <IncompleteSizeProfilesReport itemOptions={itemOptions} itemOptionsLoading={itemOptionsLoading} /> },
    { key: 'transfers', label: 'Transfers', children: <TransferReport itemOptions={itemOptions} itemOptionsLoading={itemOptionsLoading} /> },
    { key: 'purchase-orders', label: 'Purchase Orders', children: <PurchaseOrderReport itemOptions={itemOptions} itemOptionsLoading={itemOptionsLoading} /> },
    { key: 'stock', label: 'Current Stock', children: <InventoryReport itemOptions={itemOptions} itemOptionsLoading={itemOptionsLoading} /> },
    { key: 'lifecycle', label: 'Lifecycle / Replacements', children: <LifecycleReport /> },
    { key: 'variance', label: 'Variance', children: <VarianceReport /> },
    { key: 'lowstock', label: 'Low Stock', children: <LowStockWidget itemOptions={itemOptions} itemOptionsLoading={itemOptionsLoading} /> },
    { key: 'due', label: 'Due Replacements', children: <DueReplacementsWidget navigate={navigate} /> },
  // Everything this screen can render is listed above; HIDDEN_TABS decides
  // which of them reach the user, so a tab is withheld without unpicking
  // its widget, its endpoint or its filters.
  ].filter((t) => !HIDDEN_TABS.has(t.key));

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader title="Uniform Reports" />
        <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} type="card" />
      </div>
    </div>
  );
};

// ==================== Current Stock Report ====================
const STOCK_FIELDS = ['warehouse', 'item', 'size', 'positions', 'itemGender', 'combined', 'groupBy'];

/** Warehouse, item and size are the three axes of every stock question ("how
 *  much of what, in which size, where"). `combined` and `groupBy` join them
 *  not because they are reached for as often but because each is a display
 *  MODE — one drops the warehouse axis, the other the size axis, and both
 *  swap the column set and change what "Total Quantity" means — and a mode
 *  must never be togglable from behind a panel the user then closes and
 *  forgets. That leaves the position multi-select and item gender, both
 *  genuine refinements, in the disclosure. */
const STOCK_PRIMARY_FIELDS = ['warehouse', 'item', 'size', 'combined', 'groupBy'];

/** The only roll-up the server offers for stock. The cleared select is the
 *  default item x size grain; the placeholder says so. */
const STOCK_GROUP_BY_OPTIONS = [{ label: 'Item (sizes summed)', value: 'item' }];

const InventoryReport = ({ itemOptions, itemOptionsLoading }) => {
  // Rows and the shape they are in are one piece of state, set together when a
  // response lands. Deriving the columns from `applied` instead would swap the
  // header the instant Apply is pressed, while the previous shape's rows are
  // still on screen for the length of the request — every cell blank under a
  // header that does not describe them. antd renders those stale rows under the
  // loading overlay, so this is visible, not theoretical.
  const [result, setResult] = useState({
    rows: [], combined: false, byItem: false, warehouseFiltered: false, sizeFiltered: false,
  });
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({});
  const [applied, setApplied] = useState({});
  // This report has no server-side pagination — antd's own Table pagination
  // only self-corrects `current` when it becomes out of range (see
  // usePagination in antd/es/table/hooks/usePagination.js: it clamps to
  // maxPage, it doesn't unconditionally reset to 1). If a filter shrinks the
  // dataset but the current page is still in range, antd would leave the
  // user parked on e.g. page 2 of a completely different result set. So we
  // control `current` ourselves and reset it to 1 on every Apply/Clear, same
  // as every paginated tab in this dashboard.
  const [page, setPage] = useState(1);

  const fetchData = useCallback(() => {
    let ignore = false;
    setLoading(true);
    // Captured from the request that is about to go out, not read at response
    // time, so a slow response can never be labelled with a newer filter's mode.
    const params = cleanParams(applied);
    const asked = {
      combined: params.combined === '1',
      byItem: params.group_by === 'item',
      warehouseFiltered: !!params.warehouse,
      sizeFiltered: !!params.size,
    };
    getInventoryReportApi(params).then((response) => {
      if (ignore) return;
      setResult({ rows: response.data?.data || [], ...asked });
    }).catch((e) => {
      if (ignore) return;
      // Covers the 400s from a bad gender or position list, whose exact server
      // message `uniformErrorMessage` pulls out of `{ "error": ... }`.
      const message = uniformErrorMessage(e, 'Failed to load stock report');
      toast.error(message, toastOpts);
      // Drop the rows too. Keeping them would leave the previous result — and
      // possibly the previous combined/plain column set — sitting under the
      // filter values now shown in the bar, so the screen would assert that
      // those filters produced this data. A toast is the wrong weight of
      // correction for that: it is gone in three seconds and the wrong table
      // stays. The mode still follows what was asked, so the header matches
      // the bar; there is simply nothing under it.
      setResult({ rows: [], ...asked, error: message });
    }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [applied]);

  useEffect(() => fetchData(), [fetchData]);

  const pinLeft = useStickyIdentity();

  // Per warehouse: one row per warehouse x item x size.
  const perWarehouseColumns = [
    // Pinned: which warehouse a stock line belongs to. The `combined` variant
    // below is three columns and never scrolls, so it stays unpinned.
    { title: 'Warehouse', dataIndex: 'warehouse_name', ...pinLeft(190) },
    { title: 'Item', dataIndex: 'uniform_item_name' },
    { title: 'Size', dataIndex: 'size_code' },
    {
      title: 'Quantity', dataIndex: 'quantity',
      render: (qty, record) => {
        const isLow = record.reorder_level > 0 && qty <= record.reorder_level;
        return <span style={{ color: isLow ? '#ff4d4f' : 'inherit', fontWeight: isLow ? 600 : 400 }}>{qty}</span>;
      }
    },
    { title: 'Reorder Level', dataIndex: 'reorder_level', render: (val) => val || '-' }
  ];

  // Combined: one row per item x size, quantity summed. The server drops
  // `warehouse_name` and `reorder_level` from these rows — reorder levels are
  // per warehouse, so a summed total has nothing to compare against and the
  // low-stock highlight above would be meaningless here. Both columns are
  // therefore absent rather than blank.
  const combinedColumns = [
    { title: 'Item', dataIndex: 'uniform_item_name' },
    { title: 'Size', dataIndex: 'size_code' },
    { title: 'Total Quantity', dataIndex: 'total_quantity' },
  ];

  // Group by item: the size axis is gone and, with it, `reorder_level` (set
  // per size). Both roll-ups sum into `total_quantity`, the same key the
  // combined view already uses for a summed figure.
  const byItemColumns = [
    { title: 'Warehouse', dataIndex: 'warehouse_name', ...pinLeft(190) },
    { title: 'Item', dataIndex: 'uniform_item_name' },
    { title: 'Total Quantity', dataIndex: 'total_quantity' },
  ];
  const combinedByItemColumns = [
    { title: 'Item', dataIndex: 'uniform_item_name' },
    { title: 'Total Quantity', dataIndex: 'total_quantity' },
  ];

  const columns = result.combined
    ? (result.byItem ? combinedByItemColumns : combinedColumns)
    : (result.byItem ? byItemColumns : perWarehouseColumns);

  return (
    <div>
      <div className="d-flex justify-content-end mb-2">
        {/* Same `applied` object as the fetch, so the workbook is always the
            sheet on screen — including which of the two column sets it uses,
            which the server picks from the same `combined` param. */}
        <ExcelExportButton framed
          apiFn={exportInventoryReportApi}
          params={cleanParams(applied)}
          filename="Uniform Inventory.xlsx"
        />
      </div>
      <ReportFilterBar
        fields={STOCK_FIELDS} primaryFields={STOCK_PRIMARY_FIELDS}
        value={draft} appliedValue={applied} onChange={setDraft}
        itemOptions={itemOptions} itemOptionsLoading={itemOptionsLoading}
        groupByOptions={STOCK_GROUP_BY_OPTIONS} groupByPlaceholder="Item & size (no grouping)"
        onApply={() => { setApplied(draft); setPage(1); }}
        onClear={() => { setDraft({}); setApplied({}); setPage(1); }}
      />
      {result.byItem && result.sizeFiltered && (
        // Same honesty as the warehouse note below: the size filter still
        // narrows what is summed, so an item total under a Size filter is a
        // one-size figure wearing an all-sizes header.
        <div className="mb-2 text-muted" style={{ fontSize: 12.5 }}>
          Item totals cover the selected size only — clear the Size filter for a total across all sizes.
        </div>
      )}
      {result.combined && result.warehouseFiltered && (
        // The warehouse filter keeps working in combined mode — it narrows what
        // is summed. That makes "Total Quantity" true but the control's label,
        // "all warehouses", false, so say which it actually is rather than let
        // the header imply a company-wide total.
        <div className="mb-2 text-muted" style={{ fontSize: 12.5 }}>
          Totals cover the selected warehouse only, not all warehouses — clear the Warehouse filter for a company-wide total.
        </div>
      )}
      <Table columns={columns} dataSource={result.rows} rowKey={(r, i) => i} loading={loading}
        pagination={{ current: page, onChange: setPage, showSizeChanger: true }} scroll={{ x: "max-content" }}
        // Without this the cleared table falls back to antd's "No data", which
        // reads as "these filters matched nothing" — the opposite of what a 400
        // means, and the one wrong conclusion the user must not draw here.
        locale={result.error ? { emptyText: result.error } : undefined} />
    </div>
  );
};

// ==================== Lifecycle Report ====================
/** Cost centre, position and employee here match the issuance's own snapshot
 *  columns — the cost centre and position the employee held at issue time —
 *  which is what the issuance report filters on too. So a roll-up row on this
 *  tab and the issuance report filtered to the same cost centre describe the
 *  same issuances, and "View employees" on a roll-up row is exactly that
 *  filter applied. Every filter narrows DEMAND only; the stock and on-order
 *  figures in the uniform grain are always company-wide. */
const LIFECYCLE_FIELDS = ['groupBy', 'casas', 'items', 'positions', 'employees', 'size'];
/** The mode, plus the two filters a replacement planner reaches for first:
 *  which cost centre is asking, and which uniform. */
const LIFECYCLE_PRIMARY_FIELDS = ['groupBy', 'casas', 'items'];

/** The tab opens in the matrix: uniforms and sizes down, one column per cost
 *  centre, totals and stock at the right — the purchaser's allocation sheet,
 *  chosen over the per-cost-centre list because it answers "how much to buy"
 *  and "who gets what" on one screen. Clearing the select falls back to the
 *  raw employee rows, which is what "no grouping" honestly means. Clear
 *  restores this, not {}. */
const LIFECYCLE_DEFAULT_FILTERS = { group_by: 'matrix' };

/** `filterKey` is the query param a roll-up row drills down through;
 *  `header` names the first column of the grouped table. The matrix and
 *  uniform grains drill through item + size (+ cost centre from a matrix
 *  cell), handled in `drillDown`. */
const LIFECYCLE_GROUP_BY_OPTIONS = [
  { label: 'Matrix (uniform × cost center)', value: 'matrix', header: 'Uniform' },
  { label: 'Uniform & size (stock vs due)', value: 'uniform', header: 'Uniform' },
  { label: 'Cost Center', value: 'cost_center', filterKey: 'cost_center', header: 'Cost Center' },
  { label: 'Position', value: 'position', filterKey: 'position', header: 'Position' },
];
const lifecycleGroupFor = (value) => LIFECYCLE_GROUP_BY_OPTIONS.find((o) => o.value === value) || null;

const SUPPLY_STATUS = {
  short: { color: 'red', label: 'Short' },
  covered_by_po: { color: 'blue', label: 'Covered by open PO' },
  sufficient: { color: 'green', label: 'Sufficient' },
};

/** The line shape POFormModal's `prefill` takes, from a uniform-grain row.
 *  Quantity is the shortfall: what is left to order after stock and open
 *  POs are counted. */
const reorderLine = (r) => ({
  uniform_item: r.uniform_item_id, uniform_item_name: r.uniform_item_name,
  size: r.size_id, size_name: r.size_name, size_code: r.size_code,
  ordered_quantity: r.shortfall,
});

const sumOf = (rows, pick) => rows.reduce((acc, r) => acc + (pick(r) || 0), 0);

const LifecycleReport = () => {
  const navigate = useNavigate();
  // Rows and the grain they came back in are one piece of state, for the
  // same reason as the stock tab: the header must describe the rows under
  // it, including while a request for the other grain is still in flight.
  const [result, setResult] = useState({ rows: [], group: null, costCenters: [], totals: null });
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(LIFECYCLE_DEFAULT_FILTERS);
  const [applied, setApplied] = useState(LIFECYCLE_DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  // `daysAhead` updates instantly while dragging (UI label).
  // `committedDays` only updates when the user releases the slider — that's
  // what actually triggers the API call, so we don't fire 100s of requests.
  const [daysAhead, setDaysAhead] = useState(90);
  const [committedDays, setCommittedDays] = useState(90);

  // Guarded with `ignore` (including inside `.finally()`, so a stale response
  // can't flip the loading state either) — the slider/preset controls refetch
  // on every change, so a slow response for an abandoned window is a real race.
  const fetchData = useCallback(() => {
    let ignore = false;
    setLoading(true);
    const params = { days_ahead: committedDays, ...cleanParams(applied) };
    // Captured from the request going out, not read at response time.
    const askedGroup = lifecycleGroupFor(params.group_by);
    getLifecycleReportApi(params).then((response) => {
      if (ignore) return;
      // The matrix carries its column list and totals beside the rows;
      // every other grain leaves them absent.
      const body = response.data || {};
      setResult({
        rows: body.data || [], group: askedGroup,
        costCenters: body.cost_centers || [], totals: body.totals || null,
      });
    }).catch((e) => {
      if (ignore) return;
      const message = uniformErrorMessage(e, 'Failed to load lifecycle report');
      toast.error(message, toastOpts);
      // Drop the rows too, so the previous grain's table cannot sit under
      // the filters now shown in the bar — see the stock tab for the full
      // argument.
      setResult({ rows: [], group: askedGroup, costCenters: [], totals: null, error: message });
    }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [applied, committedDays]);

  useEffect(() => fetchData(), [fetchData]);

  const pinLeft = useStickyIdentity();

  // Pinned lane widths shared by every uniform-first view here. Narrow on
  // purpose: the lanes cost the store columns room in the matrix.
  // Shared with every other uniform-name column in the module.
  const SIZE_LANE = 80;

  const isMatrix = result.group?.value === 'matrix';
  const isUniformGrain = result.group?.value === 'uniform';
  // The two grains that set demand against stock and can reorder.
  const hasSupply = isMatrix || isUniformGrain;

  /** A roll-up row becomes the detail view filtered to that group. Written to
   *  both draft and applied so the bar shows the filter it just applied. A
   *  uniform or matrix row filters by item and, when the row has one, size;
   *  a matrix CELL adds the cost centre it sits under. */
  const drillDown = (record, costCenter, targetGrain) => {
    const next = { ...applied, group_by: targetGrain };
    if (hasSupply) {
      next.uniform_item = String(record.uniform_item_id);
      next.size = record.size_id ? String(record.size_id) : undefined;
      if (costCenter && costCenter.id !== null) next.cost_center = String(costCenter.id);
    } else {
      next[result.group.filterKey] = String(record.group_id);
    }
    setDraft(next);
    setApplied(next);
    setPage(1);
  };

  const shortRows = hasSupply ? result.rows.filter((r) => r.shortfall > 0) : [];

  /** Hands the short lines to the purchase-order screen, which opens its
   *  New PO form seeded with them. */
  const reorder = (rows) => {
    navigate('/uniform-management/purchase-orders', {
      state: { prefillLines: rows.map(reorderLine) },
    });
  };

  // Every view on this tab is uniform-first now, the raw rows included: the
  // reader arrives here from a uniform or a matrix cell, so the uniform and
  // size are what stay put while the employee columns scroll.
  const detailColumns = [
    { title: 'Uniform', dataIndex: 'uniform_item_name', ...pinLeft(UNIFORM_LANE), ellipsis: { showTitle: true } },
    { title: 'Size', dataIndex: 'size_code', ...pinLeft(SIZE_LANE, { secondary: true }) },
    { title: 'Employee', dataIndex: 'employee_name' },
    { title: 'Emp No', dataIndex: 'emp_no' },
    { title: 'Cost Center', dataIndex: 'cost_center_name', render: (v) => v || '-' },
    { title: 'Position', dataIndex: 'position_name', render: (v) => v || '-' },
    { title: 'Qty', dataIndex: 'quantity' },
    { title: 'Issued Date', dataIndex: 'issuance_date', render: (d) => formatDateHandler(d) },
    { title: 'Replacement Date', dataIndex: 'next_replacement_date', render: (d) => formatDateHandler(d) },
    {
      title: 'Days Until', dataIndex: 'days_until_replacement',
      render: (days) => (
        <span style={{ color: days < 0 ? '#ff4d4f' : days <= 14 ? '#faad14' : '#52c41a', fontWeight: 600 }}>
          {days < 0 ? `${Math.abs(days)} overdue` : `${days} days`}
        </span>
      )
    },
    {
      title: 'Status', dataIndex: 'status',
      render: (status) => <Tag color={status === 'overdue' ? 'red' : 'orange'}>{status}</Tag>
    }
  ];

  const emphasis = (color) => (v) => (
    <span style={{ color: v > 0 ? color : 'inherit', fontWeight: v > 0 ? 600 : 400 }}>{v}</span>
  );

  // Two action columns rather than one shared cell: a Reorder button that
  // only some rows carry would otherwise shove "View employees" sideways on
  // exactly those rows, so the link never lines up down the table.
  const viewEmployeesColumn = (className) => ({
    title: '', key: 'view', width: 130, className,
    render: (_, record) => (
      <button type="button" className="btn btn-link btn-sm p-0" onClick={() => drillDown(record)}>
        View employees
      </button>
    ),
  });
  const reorderColumn = (className) => ({
    title: 'Reorder', key: 'reorder', width: 120, align: 'right', className,
    render: (_, record) => (record.shortfall > 0
      ? <Button label={`Reorder ${record.shortfall}`} className="btn btn-sm btn-primary" onClick={() => reorder([record])} />
      : <span className="text-muted">–</span>),
  });

  // Size shows its code alone in the uniform-first views: the pinned lane
  // stays narrow and the codes are what the stores order by.
  const sizeCode = (v) => v || '-';

  // Roll-up by cost centre / position: six columns, never scrolls, so
  // nothing is pinned. Counts are uniforms (quantity), not people —
  // `employees` is there so 12 uniforms across 4 people reads differently
  // from 12 across 12.
  const groupedColumns = [
    { title: result.group?.header || 'Group', dataIndex: 'group_name' },
    { title: 'Employees', dataIndex: 'employees' },
    { title: 'Uniforms Due', dataIndex: 'uniforms', render: (v) => <strong>{v}</strong> },
    { title: 'Overdue', dataIndex: 'overdue_uniforms', render: emphasis('#ff4d4f') },
    { title: 'Upcoming', dataIndex: 'upcoming_uniforms', render: emphasis('#faad14') },
    {
      title: '', key: 'drill', width: 240,
      // The "Unassigned" bucket has no id to filter by, so it has no link.
      render: (_, record) => record.group_id === null ? null : (
        <div className="d-flex gap-3 align-items-center">
          {/* What this cost centre (or position) needs, size by size, against
              stock — the uniform grain filtered to it. */}
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => drillDown(record, null, 'uniform')}>
            View uniforms
          </button>
          <button type="button" className="btn btn-link btn-sm p-0" onClick={() => drillDown(record)}>
            View employees
          </button>
        </div>
      )
    },
  ];

  // Demand vs supply per (item, size): the reorder view. Pinned: the uniform
  // names the row and there are twelve columns.
  const uniformColumns = [
    { title: 'Uniform', dataIndex: 'uniform_item_name', ...pinLeft(UNIFORM_LANE), ellipsis: { showTitle: true } },
    { title: 'Size', dataIndex: 'size_code', render: sizeCode, ...pinLeft(SIZE_LANE, { secondary: true }) },
    { title: 'Employees', dataIndex: 'employees' },
    { title: 'Uniforms Due', dataIndex: 'uniforms', render: (v) => <strong>{v}</strong> },
    { title: 'Overdue', dataIndex: 'overdue_uniforms', render: emphasis('#ff4d4f') },
    { title: 'Upcoming', dataIndex: 'upcoming_uniforms', render: emphasis('#faad14') },
    { title: 'Available', dataIndex: 'available', render: (v) => <strong>{v}</strong> },
    { title: 'On Order', dataIndex: 'on_order', render: (v) => (v > 0 ? v : '-') },
    { title: 'Shortfall', dataIndex: 'shortfall', render: emphasis('#ff4d4f') },
    {
      title: 'Status', dataIndex: 'status',
      render: (status) => {
        const chip = SUPPLY_STATUS[status] || { color: 'default', label: status };
        return <Tag color={chip.color}>{chip.label}</Tag>;
      }
    },
    viewEmployeesColumn(),
    reorderColumn(),
  ];

  /** JSON keys the server uses for a cost centre's cell. */
  const cellKey = (cc) => (cc.id === null ? 'unassigned' : String(cc.id));

  // The matrix: one demand cell per cost centre between the identity columns
  // and the supply columns. A cell is a link into that cost centre's
  // employees; the small red figure is how many of the cell are overdue.
  // The supply block is set apart by a rule and a tint (see
  // lifecycleMatrix.css); the classes ride on `className`, which antd puts
  // on the header cell, every body cell and — passed explicitly below —
  // the totals row too.
  const SUPPLY = 'lifecycle-matrix-supply';
  const SUPPLY_FIRST = `${SUPPLY} lifecycle-matrix-supply-first`;

  const matrixColumns = [
    // Both identity columns pinned: a matrix is as wide as the estate has
    // stores, and a size without its uniform, or a uniform without its size,
    // names nothing.
    { title: 'Uniform', dataIndex: 'uniform_item_name', ...pinLeft(UNIFORM_LANE), ellipsis: { showTitle: true } },
    { title: 'Size', dataIndex: 'size_code', render: sizeCode, ...pinLeft(SIZE_LANE, { secondary: true }) },
    ...result.costCenters.map((cc) => ({
      // Headed by the store name (codes were tried and read worse); the
      // header wraps rather than widening the column.
      title: <span className="lifecycle-matrix-store">{cc.name}</span>,
      key: `cc-${cellKey(cc)}`, align: 'right',
      render: (_, r) => {
        const cell = r.cells?.[cellKey(cc)];
        if (!cell || !cell.due) return <span className="text-muted">–</span>;
        // The due count in plain ink (a link, but not dressed as one — the
        // blue read as a second value), the overdue share as a red badge
        // that says what it is on hover.
        return (
          <button type="button" className="btn btn-link btn-sm p-0 text-reset" style={{ fontVariantNumeric: 'tabular-nums', textDecoration: 'none' }}
            title={`View ${cc.name} employees due for this size`} onClick={() => drillDown(r, cc)}>
            <strong>{cell.due}</strong>
            {cell.overdue > 0 && (
              <span title={`${cell.overdue} of ${cell.due} already overdue`} style={{
                marginLeft: 5, fontSize: 10.5, fontWeight: 700, lineHeight: '16px', color: '#fff',
                background: '#ff4d4f', borderRadius: 8, padding: '0 5px', display: 'inline-block', verticalAlign: 'text-bottom',
              }}>{cell.overdue}</span>
            )}
          </button>
        );
      },
    })),
    { title: 'Total Due', dataIndex: 'uniforms', align: 'right', render: (v) => <strong>{v}</strong>, className: SUPPLY_FIRST },
    { title: 'Available', dataIndex: 'available', align: 'right', render: (v) => <strong>{v}</strong>, className: SUPPLY },
    { title: 'On Order', dataIndex: 'on_order', align: 'right', render: (v) => (v > 0 ? v : '-'), className: SUPPLY },
    { title: 'Shortfall', dataIndex: 'shortfall', align: 'right', render: emphasis('#ff4d4f'), className: SUPPLY },
    {
      title: 'Status', dataIndex: 'status', className: SUPPLY,
      render: (status) => {
        const chip = SUPPLY_STATUS[status] || { color: 'default', label: status };
        return <Tag color={chip.color}>{chip.label}</Tag>;
      }
    },
    viewEmployeesColumn(SUPPLY),
    reorderColumn(SUPPLY),
  ];

  // Column totals under the matrix: one cell per column, in column order.
  const matrixSummary = () => {
    const t = result.totals;
    if (!isMatrix || !t || !result.rows.length) return null;
    const cells = [
      <Table.Summary.Cell index={0} key="label"><strong>All uniforms</strong></Table.Summary.Cell>,
      <Table.Summary.Cell index={1} key="size" />,
      ...result.costCenters.map((cc, i) => (
        <Table.Summary.Cell index={2 + i} key={`cc-${cellKey(cc)}`} align="right">
          <strong>{t.by_cost_center?.[cellKey(cc)]?.due ?? 0}</strong>
        </Table.Summary.Cell>
      )),
    ];
    const base = 2 + result.costCenters.length;
    cells.push(
      <Table.Summary.Cell index={base} key="due" align="right" className={SUPPLY_FIRST}><strong>{t.uniforms}</strong></Table.Summary.Cell>,
      <Table.Summary.Cell index={base + 1} key="avail" align="right" className={SUPPLY}>{t.available}</Table.Summary.Cell>,
      <Table.Summary.Cell index={base + 2} key="order" align="right" className={SUPPLY}>{t.on_order > 0 ? t.on_order : '-'}</Table.Summary.Cell>,
      <Table.Summary.Cell index={base + 3} key="short" align="right" className={SUPPLY}>{emphasis('#ff4d4f')(t.shortfall)}</Table.Summary.Cell>,
      <Table.Summary.Cell index={base + 4} key="status" className={SUPPLY} />,
      <Table.Summary.Cell index={base + 5} key="view" className={SUPPLY} />,
      <Table.Summary.Cell index={base + 6} key="reorder" className={SUPPLY} />,
    );
    return <Table.Summary fixed><Table.Summary.Row>{cells}</Table.Summary.Row></Table.Summary>;
  };

  const columns = isMatrix ? matrixColumns : isUniformGrain ? uniformColumns : (result.group ? groupedColumns : detailColumns);
  const rowKey = hasSupply
    ? (r) => `${r.uniform_item_id}:${r.size_id ?? 'none'}`
    : result.group
      ? (r) => (r.group_id === null ? 'unassigned' : r.group_id)
      : (r) => r.id;

  // Stat cards count UNIFORMS in every grain, so switching Group by never
  // changes what a number means.
  const totalUniforms = result.group ? sumOf(result.rows, (r) => r.uniforms) : sumOf(result.rows, (r) => r.quantity);
  const overdueUniforms = result.group
    ? sumOf(result.rows, (r) => r.overdue_uniforms)
    : sumOf(result.rows, (r) => (r.status === 'overdue' ? r.quantity : 0));
  const upcomingUniforms = totalUniforms - overdueUniforms;
  const availableUniforms = sumOf(result.rows, (r) => r.available);
  const shortfallUniforms = sumOf(shortRows, (r) => r.shortfall);

  const presets = [30, 60, 90, 180, 365];

  const StatCard = ({ label, value, color, icon, hint }) => (
    <div className="p-3 d-flex align-items-center gap-3 h-100" style={{
      background: '#fff', border: '1px solid #F1F2F4', borderRadius: 12, minWidth: 0,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: `${color}15`, color: color,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
      }}>
        <i className={icon} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#1F1F1F', lineHeight: 1.2 }}>
          {loading ? <i className="fa fa-spinner fa-spin" style={{ fontSize: 14 }} /> : value}
        </div>
        {hint && !loading && <div className="text-muted" style={{ fontSize: 11 }}>{hint}</div>}
      </div>
    </div>
  );

  const statCol = hasSupply ? 'col-md-3 col-sm-6' : 'col-md-4';

  return (
    <div>
      <div className="row g-3 mb-4">
        <div className={statCol}>
          <StatCard label="Uniforms due in window" value={totalUniforms} color="#2563EB" icon="fa fa-list"
            hint={isMatrix && result.costCenters.length ? `across ${result.costCenters.length} cost center${result.costCenters.length === 1 ? '' : 's'}` : undefined} />
        </div>
        {hasSupply && (
          <div className={statCol}>
            <StatCard label="Available (all warehouses)" value={availableUniforms} color="#059669" icon="fa fa-cubes"
              hint="Stock of the sizes due" />
          </div>
        )}
        {hasSupply && (
          <div className={statCol}>
            <StatCard label="Shortfall to order" value={shortfallUniforms} color="#DC2626" icon="fa fa-shopping-cart"
              hint={shortRows.length ? `${shortRows.length} size${shortRows.length === 1 ? '' : 's'} short` : 'Stock and open POs cover the window'} />
          </div>
        )}
        <div className={statCol}>
          <StatCard label="Overdue" value={overdueUniforms} color={hasSupply ? '#F59E0B' : '#DC2626'} icon="fa fa-exclamation-triangle" />
        </div>
        {!hasSupply && (
          <div className={statCol}>
            <StatCard label="Upcoming" value={upcomingUniforms} color="#F59E0B" icon="fa fa-clock" />
          </div>
        )}
      </div>

      <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
        {shortRows.length > 0 && (
          // One PO for every short line in view — the bulk form of the
          // per-row Reorder button, for the reader who has already
          // narrowed the window and filters to the order they mean to place.
          <Button
            label={`Create PO for ${shortRows.length} short size${shortRows.length === 1 ? '' : 's'}`}
            className="btn btn-primary" onClick={() => reorder(shortRows)} />
        )}
        {/* Same params as the fetch, so the workbook is the sheet on screen,
            grain included. */}
        <ExcelExportButton framed
          apiFn={exportLifecycleReportApi}
          params={{ days_ahead: committedDays, ...cleanParams(applied) }}
          filename="Uniform Lifecycle.xlsx"
        />
      </div>

      <div className="p-3 mb-3" style={{
        background: '#fff', border: '1px solid #F1F2F4', borderRadius: 12,
      }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <div>
            <div className="fw-semibold" style={{ fontSize: 14 }}>Look-ahead window</div>
            <small className="text-muted">
              Show replacements due within the next <strong>{daysAhead}</strong> day{daysAhead === 1 ? '' : 's'}
            </small>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            {presets.map(p => (
              <button
                key={p}
                type="button"
                className={`btn btn-sm ${committedDays === p ? 'btn-primary' : 'btn-light'}`}
                onClick={() => { setDaysAhead(p); setCommittedDays(p); }}
              >
                {p === 365 ? '1y' : `${p}d`}
              </button>
            ))}
          </div>
        </div>
        <Slider
          min={7}
          max={365}
          value={daysAhead}
          onChange={setDaysAhead}
          onChangeComplete={setCommittedDays}
          tooltip={{ formatter: (v) => `${v} days` }}
        />
      </div>

      <ReportFilterBar
        fields={LIFECYCLE_FIELDS} primaryFields={LIFECYCLE_PRIMARY_FIELDS}
        value={draft} appliedValue={applied} onChange={setDraft}
        groupByOptions={LIFECYCLE_GROUP_BY_OPTIONS} groupByPlaceholder="No grouping (one row per uniform issued)"
        onApply={() => { setApplied(draft); setPage(1); }}
        onClear={() => { setDraft(LIFECYCLE_DEFAULT_FILTERS); setApplied(LIFECYCLE_DEFAULT_FILTERS); setPage(1); }}
      />
      {hasSupply && (
        <div className="mb-2 text-muted" style={{ fontSize: 12.5 }}>
          {isMatrix && (
            <span>
              Store columns show the uniforms due at that store. A red badge is how many of those are already overdue. Click a number for that store's employees.{' '}
            </span>
          )}
          Available and On Order are company-wide (every warehouse, every open PO); the filters above narrow only what is due.
        </div>
      )}

      <Table columns={columns} dataSource={result.rows} rowKey={rowKey} loading={loading}
        size="small" className="lifecycle-table"
        pagination={{ current: page, onChange: setPage, showSizeChanger: true }} scroll={{ x: "max-content" }}
        summary={matrixSummary}
        locale={result.error ? { emptyText: result.error } : undefined} />
    </div>
  );
};

// ==================== Variance Report ====================
const VarianceReport = () => {
  const { warehouseOptions, warehousesLoading } = useUniformMgmt();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterWarehouse, setFilterWarehouse] = useState(null);

  // Guarded with `ignore` so a slow response for a warehouse the user has
  // since changed away from can't clobber newer results, including the
  // loading-state reset (guarded inside `.finally()` too).
  const fetchData = useCallback(() => {
    let ignore = false;
    setLoading(true);
    const params = {};
    if (filterWarehouse) params.warehouse = filterWarehouse.value;
    getVarianceReportApi(params).then((response) => {
      if (ignore) return;
      setData(response.data?.data || []);
    }).catch((e) => {
      if (ignore) return;
      toast.error(uniformErrorMessage(e, 'Failed to load variance report'), toastOpts);
    }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [filterWarehouse]);

  useEffect(() => fetchData(), [fetchData]);

  const pinLeft = useStickyIdentity();

  const columns = [
    // Pinned: the stock take a variance line came from.
    { title: 'Stock Take', dataIndex: 'stock_take_id', ...pinLeft(130) },
    { title: 'Warehouse', dataIndex: 'warehouse_name' },
    { title: 'Date', dataIndex: 'conducted_date', render: (d) => formatDateHandler(d) },
    { title: 'Item', dataIndex: 'uniform_item_name' },
    { title: 'Size', dataIndex: 'size_code' },
    { title: 'System Qty', dataIndex: 'system_quantity' },
    { title: 'Physical Qty', dataIndex: 'physical_quantity' },
    {
      title: 'Variance', dataIndex: 'variance',
      render: (val) => (
        <span style={{ color: val < 0 ? '#ff4d4f' : '#52c41a', fontWeight: 600 }}>
          {val > 0 ? `+${val}` : val}
        </span>
      )
    }
  ];

  return (
    <div>
      <div className="row mb-3 align-items-center">
        <div className="col-md-3">
          <FilterSelect isClearable onChange={setFilterWarehouse} optionLabel="label" optionValue="value"
            options={warehouseOptions} value={filterWarehouse} placeholder="Filter by Warehouse" isLoading={warehousesLoading} />
        </div>
        <div className="col-md-9 text-end">
          <ExcelExportButton framed
            apiFn={exportVarianceReportApi}
            params={filterWarehouse ? { warehouse: filterWarehouse.value } : {}}
            filename="Uniform Variance.xlsx"
          />
        </div>
      </div>
      <Table columns={columns} dataSource={data} rowKey={(r, i) => i} loading={loading}
        pagination={{ showSizeChanger: true }} scroll={{ x: "max-content" }} />
    </div>
  );
};

// ==================== Low Stock Widget ====================
const LOW_STOCK_FIELDS = ['warehouse', 'item'];

/** Stock lines at or below their reorder level, per warehouse. The list is
 *  only as useful as the thresholds behind it, so an empty result is
 *  explained from the server's `reorder_levels_set` count rather than
 *  declared healthy: no thresholds configured is a setup gap, not good news. */
/** Exported for its tests: the tab that shows it is currently in
 *  HIDDEN_TABS, but the widget is live code and stays covered. */
export const LowStockWidget = ({ itemOptions, itemOptionsLoading }) => {
  // `loaded` gates every verdict below: before the first response there is
  // nothing to be healthy or low about, and a green banner on an unloaded
  // page asserts something no request has yet confirmed.
  const [result, setResult] = useState({ rows: [], thresholds: null, loaded: false });
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({});
  const [applied, setApplied] = useState({});
  const [page, setPage] = useState(1);

  const fetchData = useCallback(() => {
    let ignore = false;
    setLoading(true);
    getLowStockApi(cleanParams(applied)).then((response) => {
      if (ignore) return;
      const body = response.data || {};
      setResult({ rows: body.data || [], thresholds: body.reorder_levels_set ?? null, loaded: true });
    }).catch((e) => {
      if (ignore) return;
      const message = uniformErrorMessage(e, 'Failed to load low stock report');
      toast.error(message, toastOpts);
      setResult({ rows: [], thresholds: null, loaded: false, error: message });
    }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [applied]);

  useEffect(() => fetchData(), [fetchData]);

  const pinLeft = useStickyIdentity();

  const columns = [
    // Pinned: which warehouse is low on this item.
    { title: 'Warehouse', dataIndex: 'warehouse_name', ...pinLeft(190) },
    { title: 'Item', dataIndex: 'uniform_item_name' },
    { title: 'Size', dataIndex: 'size_name', render: (name, record) => (name ? `${name} (${record.size_code})` : '-') },
    {
      title: 'Quantity', dataIndex: 'quantity',
      render: (qty) => <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{qty}</span>
    },
    { title: 'Reorder Level', dataIndex: 'reorder_level' },
    { title: 'Short by', key: 'gap', render: (_, r) => Math.max(0, r.reorder_level - r.quantity) },
  ];

  const filtered = Object.keys(cleanParams(applied)).length > 0;

  let banner = null;
  if (result.loaded && result.rows.length > 0) {
    banner = (
      <div className="alert alert-danger mb-3">
        <i className="fa fa-exclamation-triangle me-2" />
        {result.rows.length} stock line{result.rows.length === 1 ? '' : 's'} at or below reorder level
      </div>
    );
  } else if (result.loaded && result.thresholds === 0) {
    banner = (
      <div className="alert alert-warning mb-3">
        <i className="fa fa-info-circle me-2" />
        No reorder levels are set on any stock line, so nothing can appear here yet. Set a reorder level on a warehouse's stock lines to be warned when they run low.
      </div>
    );
  } else if (result.loaded) {
    banner = (
      <div className="alert alert-success mb-3">
        <i className="fa fa-check me-2" />
        {filtered ? 'Nothing at or below reorder level for these filters' : 'All stock levels are healthy'}
        {result.thresholds ? ` (${result.thresholds} line${result.thresholds === 1 ? '' : 's'} have a reorder level)` : ''}
      </div>
    );
  }

  return (
    <div>
      <p className="text-muted mb-2" style={{ fontSize: 12.5 }}>
        Stock lines that have fallen to or below the reorder level set on them, per warehouse.
      </p>
      <ReportFilterBar
        fields={LOW_STOCK_FIELDS}
        value={draft} appliedValue={applied} onChange={setDraft}
        itemOptions={itemOptions} itemOptionsLoading={itemOptionsLoading}
        onApply={() => { setApplied(draft); setPage(1); }}
        onClear={() => { setDraft({}); setApplied({}); setPage(1); }}
      />
      {banner}
      <Table columns={columns} dataSource={result.rows} rowKey={(r) => r.id} loading={loading}
        pagination={{ current: page, onChange: setPage, showSizeChanger: true }} scroll={{ x: "max-content" }}
        locale={result.error ? { emptyText: result.error } : undefined} />
    </div>
  );
};

// ==================== Due Replacements Widget ====================
/** The window select writes `window` into the draft; `dueParams` turns it
 *  into what the endpoint reads. "overdue" is a flag, the rest a horizon. */
const DUE_WINDOW_OPTIONS = [
  { label: 'Overdue only', value: 'overdue' },
  { label: 'Next 7 days', value: '7' },
  { label: 'Next 30 days', value: '30' },
  { label: 'Next 60 days', value: '60' },
  { label: 'Next 90 days', value: '90' },
];
const DUE_FIELDS = ['window', 'casas', 'positions'];
const DUE_DEFAULT_FILTERS = { window: '30' };

const dueParams = (filters) => {
  const { window: win, ...rest } = cleanParams(filters);
  const params = { ...rest };
  if (win === 'overdue') {
    params.overdue = 1;
    // Overdue is "already past", so the horizon is irrelevant; 0 keeps the
    // request honest about that rather than sending the default 30.
    params.days_ahead = 0;
  } else {
    params.days_ahead = Number(win) || 30;
  }
  return params;
};

/** The scope the bulk issuance screen loads from these rows: every cost
 *  centre and position they span (as options, so nothing waits on lists),
 *  the employees themselves, and the replacement statuses that match the
 *  window in force. */
const reissueScope = (rows, filters) => {
  const uniq = (pairs) => {
    const seen = new Map();
    pairs.forEach(([value, label]) => { if (value != null && !seen.has(value)) seen.set(value, { label, value }); });
    return [...seen.values()];
  };
  const overdueOnly = filters.window === 'overdue';
  return {
    casas: uniq(rows.map((r) => [r.cost_center, r.casa_name])),
    positions: uniq(rows.map((r) => [r.position, r.position_name])),
    employees: uniq(rows.map((r) => [r.company_user, `${r.company_user_name}${r.company_user_emp_no ? ` (${r.company_user_emp_no})` : ''}`])),
    due: overdueOnly ? ['overdue'] : ['overdue', 'due_soon'],
    dueDays: overdueOnly ? undefined : Number(filters.window) || 30,
  };
};

const DueReplacementsWidget = ({ navigate }) => {
  const [result, setResult] = useState({ rows: [], loaded: false });
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(DUE_DEFAULT_FILTERS);
  const [applied, setApplied] = useState(DUE_DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  const fetchData = useCallback(() => {
    let ignore = false;
    setLoading(true);
    getDueReplacementsApi(dueParams(applied)).then((response) => {
      if (ignore) return;
      setResult({ rows: response.data?.data || [], loaded: true });
    }).catch((e) => {
      if (ignore) return;
      const message = uniformErrorMessage(e, 'Failed to load due replacements report');
      toast.error(message, toastOpts);
      setResult({ rows: [], loaded: false, error: message });
    }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [applied]);

  useEffect(() => fetchData(), [fetchData]);

  const pinLeft = useStickyIdentity();

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const daysUntil = (iso) => Math.round((new Date(iso).setHours(0, 0, 0, 0) - today) / 86400000);
  const isOverdue = (r) => daysUntil(r.next_replacement_date) < 0;

  /** Opens bulk issuance scoped to these rows' people and their due items. */
  const reissue = (rows) => {
    navigate('/uniform-management/bulk-issuance', { state: { scope: reissueScope(rows, applied) } });
  };

  const columns = [
    // Pinned: whose replacement is due.
    { title: 'Employee', dataIndex: 'company_user_name', ...pinLeft(180) },
    { title: 'Emp No', dataIndex: 'company_user_emp_no' },
    { title: 'Cost Center', dataIndex: 'casa_name', render: (v) => v || '-' },
    { title: 'Position', dataIndex: 'position_name', render: (v) => v || '-' },
    { title: 'Uniform', dataIndex: 'uniform_item_name', ...truncatedText(UNIFORM_LANE) },
    { title: 'Size', render: (_, r) => r.size_detail?.code || r.size_code || '-' },
    { title: 'Qty', dataIndex: 'quantity' },
    { title: 'Issued Date', dataIndex: 'issuance_date', render: (d) => formatDateHandler(d) },
    { title: 'Replacement Due', dataIndex: 'next_replacement_date', render: (d) => formatDateHandler(d) },
    {
      title: 'Days', key: 'days',
      render: (_, r) => {
        const days = daysUntil(r.next_replacement_date);
        return (
          <span style={{ color: days < 0 ? '#ff4d4f' : days <= 7 ? '#faad14' : '#52c41a', fontWeight: 600 }}>
            {days < 0 ? `${Math.abs(days)} overdue` : days === 0 ? 'today' : `${days} days`}
          </span>
        );
      }
    },
    {
      title: '',
      render: (_, record) => (
        <Button label="Re-issue" className="btn btn-sm btn-primary" onClick={() => reissue([record])} />
      )
    }
  ];

  const overdueCount = result.rows.filter(isOverdue).length;
  const windowLabel = (DUE_WINDOW_OPTIONS.find((o) => o.value === applied.window) || DUE_WINDOW_OPTIONS[2]).label.toLowerCase();

  let banner = null;
  if (result.loaded && result.rows.length > 0) {
    banner = (
      <div className="alert alert-warning mb-3">
        <i className="fa fa-clock-o me-2" />
        {result.rows.length} uniform{result.rows.length === 1 ? '' : 's'} due for replacement ({windowLabel})
        {overdueCount > 0 && <strong> · {overdueCount} already overdue</strong>}
      </div>
    );
  } else if (result.loaded) {
    banner = (
      <div className="alert alert-success mb-3">
        <i className="fa fa-check me-2" />
        No replacements due ({windowLabel}{Object.keys(cleanParams(applied)).length > 1 ? ', with these filters' : ''})
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
        {result.rows.length > 0 && (
          // Everything on screen in one bulk issuance: the same people,
          // cost centres, positions and window, loaded as its scope.
          <Button label={`Re-issue all (${result.rows.length})`} className="btn btn-primary"
            onClick={() => reissue(result.rows)} />
        )}
        <ExcelExportButton framed
          apiFn={exportDueReplacementsApi}
          params={dueParams(applied)}
          filename="Uniform Due Replacements.xlsx"
        />
      </div>
      <ReportFilterBar
        fields={DUE_FIELDS}
        value={draft} appliedValue={applied} onChange={setDraft}
        extraSelects={[{ field: 'window', key: 'window', label: 'Due', options: DUE_WINDOW_OPTIONS, placeholder: 'Next 30 days' }]}
        onApply={() => { setApplied(draft); setPage(1); }}
        onClear={() => { setDraft(DUE_DEFAULT_FILTERS); setApplied(DUE_DEFAULT_FILTERS); setPage(1); }}
      />
      {banner}
      <Table columns={columns} dataSource={result.rows} rowKey={(r) => r.id} loading={loading}
        size="small" className="lifecycle-table"
        rowClassName={(r) => (isOverdue(r) ? 'due-row-overdue' : '')}
        pagination={{ current: page, onChange: setPage, showSizeChanger: true }} scroll={{ x: "max-content" }}
        locale={result.error ? { emptyText: result.error } : undefined} />
    </div>
  );
};

export default ReportsDashboard;
